// @flow
import * as React from 'react'
import RadioField from 'mz-ui-kit/form/RadioField'
import shallowEqual from 'mz-utils/shallowEqual'
import { FormattedMessage } from 'mz-intl'
import type { Input, Meta } from 'mz-ui-kit/core/BaseField'
import { wrapWithField } from '../../Field'
import { ERROR_CODES, StripeError } from '../StripeError'
import CreditcardIcon from '../assets/general-cc'
import messages from '../messages.intl'
import {
  PAY_WITH_INTENT,
  ASYNC_PAYMENT_METHOD,
  REQUEST_BUTTON_APPLEPAY,
  REQUEST_BUTTON_OTHER,
  REQUEST_BUTTON_GOOGLEPAY,
} from '../constants'
import {
  Container,
  BrandContainer,
  ExistingLabel,
} from '../CreditCardItem/elements'
import { ApplepayIcon, GooglepayIcon } from './elements'

// Types
import type { MethodProps } from '..'
import type { PaymentMethodValue } from '../PaymentMethodsList'

export type Props = MethodProps & {
  input: Input,
  meta: Meta,
}

// Constants
const TYPE_ICON = {
  [REQUEST_BUTTON_APPLEPAY]: ApplepayIcon,
  [REQUEST_BUTTON_OTHER]: CreditcardIcon,
  [REQUEST_BUTTON_GOOGLEPAY]: GooglepayIcon,
}
const TYPE_NAME = {
  [REQUEST_BUTTON_APPLEPAY]: messages.PAY_WITH_APPLEPAY,
  [REQUEST_BUTTON_OTHER]: messages.PAY_WITH_YOUR_BROWSER,
  [REQUEST_BUTTON_GOOGLEPAY]: messages.PAY_WITH_GOOGLEPAY,
}

export class PaymentRequestMethod extends React.Component<Props> {
  iteration = 0

  unmounted = false

  componentWillUnmount() {
    this.unmounted = true
  }

  createPaymentIntent = (): Promise<PaymentMethodValue> => {
    const {
      currency,
      config,
      paymentIntentActions,
      paymentRequest,
    } = this.props
    const activeIteration = ++this.iteration

    return new Promise((resolve, reject) => {
      let finalized = false
      const { request } = paymentRequest || {}
      const self = this

      if (!request) {
        reject(new StripeError(ERROR_CODES.threed_secure_declined))
        return
      }

      async function handlePaymentMethod(event) {
        try {
          const paymentIntent = await paymentIntentActions.authorizePaymentIntent(
            { paymentMethodId: event.paymentMethod.id }
          )

          if (
            !self.unmounted &&
            activeIteration === self.iteration &&
            paymentIntent
          ) {
            event.complete('success')
            resolve({
              type: PAY_WITH_INTENT,
              intent: paymentIntent,
              origin: config.origin,
              currency,
            })
          } else {
            event.complete('fail')
            reject(new StripeError(ERROR_CODES.threed_secure_declined))
            paymentIntentActions.resetIntent()
          }
        } catch (e) {
          event.complete('fail')
          reject(e)
          paymentIntentActions.resetIntent()
        } finally {
          finalizePaymentAttempt()
        }
      }

      function handlePaymentCancel() {
        if (!finalized) {
          reject(new StripeError(ERROR_CODES.threed_secure_declined))
        }
        finalizePaymentAttempt()
      }

      function finalizePaymentAttempt() {
        finalized = true
        request.off('paymentmethod', handlePaymentMethod)
        request.off('cancel', handlePaymentCancel)
      }

      request.once('paymentmethod', handlePaymentMethod)
      request.once('cancel', handlePaymentCancel)
      request.show()
    })
  }

  render() {
    const { input, paymentRequest, shouldSecurePayment } = this.props
    if (!paymentRequest) return null

    const { type } = paymentRequest
    const activeValue = {
      type: ASYNC_PAYMENT_METHOD,
      getOption: this.createPaymentIntent,
    }
    const active = !!input && shallowEqual(input.value, activeValue)
    const disabled = shouldSecurePayment
    const IconComp = TYPE_ICON[type] || TYPE_ICON.other
    const nameMessage = TYPE_NAME[type] || TYPE_NAME.other

    const handleContainerClick = () => {
      const { onChange } = input
      if (active) {
        return
      }
      onChange && onChange(activeValue)
    }

    return (
      <Container
        $active={active}
        $disabled={disabled}
        onClick={handleContainerClick}
      >
        <RadioField
          {...this.props}
          disabled={disabled}
          activeValue={activeValue}
          inactiveValue={null}
        />
        <BrandContainer>
          <IconComp />
        </BrandContainer>
        <ExistingLabel>
          <FormattedMessage message={nameMessage} />
        </ExistingLabel>
      </Container>
    )
  }
}

export default wrapWithField(PaymentRequestMethod)
